import { CleanupChannelMapping, CleanupInput, CleanupLabelRow, CleanupSuggestion } from 'app/types/attribution'
import { defaultChannelMappings } from 'lib/channelDefaults'

/**
 * Detection is exact-match-after-normalization, a vendor table, and domain roots. Edit distance
 * and prefix matching would produce confident nonsense: "direct" and "direct this is a test of
 * length" share a prefix and are unrelated.
 */

/** The API's stand-in for a source or medium that was never recorded, not a label anyone typed. */
const UNKNOWN_BUCKET = `Unknown`

/**
 * Deliberately short. Each entry is a claim about someone else's data, and the canonical side has
 * to be a value `defaultChannelMappings` already keys on, or the rename fixes nothing.
 */
const VENDOR_SOURCES: Record<string, string> = {
  'google ads': `google`,
  googleads: `google`,
  adwords: `google`,
  'google adwords': `google`,
  gads: `google`,
  fb: `facebook`,
  'fb ads': `facebook`,
  'facebook ads': `facebook`,
  meta: `facebook`,
  'meta ads': `facebook`,
  ig: `instagram`,
  insta: `instagram`,
  'instagram ads': `instagram`,
  'bing ads': `bing`,
  microsoft: `bing`,
  'microsoft ads': `bing`,
  'microsoft advertising': `bing`,
  msn: `bing`,
  yt: `youtube`,
  'youtube ads': `youtube`,
  'tik tok': `tiktok`,
  'tiktok ads': `tiktok`,
  'linked in': `linkedin`,
  'linkedin ads': `linkedin`,
}

const SECOND_LEVEL_TLDS = new Set([`co`, `com`, `net`, `org`, `ac`, `gov`, `edu`])

export function normalizeLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^[([{]+|[)\]}]+$/g, ``)
    .replace(/[_.\-+]+/g, ` `)
    .replace(/\s+/g, ` `)
    .trim()
}

export function lookupChannel(source: string, medium: string, mappings: CleanupChannelMapping[]): string | undefined {
  const normalizedSource = normalizeLabel(source)
  const normalizedMedium = normalizeLabel(medium)

  const configured = mappings.find(
    (mapping) =>
      !!mapping.source &&
      !!mapping.medium &&
      normalizeLabel(mapping.source) === normalizedSource &&
      normalizeLabel(mapping.medium) === normalizedMedium,
  )
  if (configured?.channel) return configured.channel

  const fallback = defaultChannelMappings.find(
    ([source, medium]) => normalizeLabel(source) === normalizedSource && normalizeLabel(medium) === normalizedMedium,
  )
  return fallback?.[2]
}

function domainRoot(value: string): string | undefined {
  const host = value.trim().toLowerCase()
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host)) return undefined

  const labels = host.split(`.`)
  let index = labels.length - 2
  if (index > 0 && SECOND_LEVEL_TLDS.has(labels[index])) index -= 1
  return labels[index]
}

function canonicalSourceFor(bucket: string): string | undefined {
  const normalized = normalizeLabel(bucket)

  const vendor = VENDOR_SOURCES[normalized]
  if (vendor) return vendor

  const root = domainRoot(bucket)
  if (!root || root === normalized) return undefined
  return VENDOR_SOURCES[root] ?? root
}

function splitPair(bucket: string): [string, string] {
  const [source, medium] = bucket.split(` / `)
  return [source ?? ``, medium ?? ``]
}

/**
 * The variant with the most leads wins: it carries the practice's history and leaves the fewest
 * rows to rewrite.
 */
function findVariantMerges(rows: CleanupLabelRow[], type: `source` | `medium`): CleanupSuggestion[] {
  const groups = new Map<string, CleanupLabelRow[]>()
  for (const row of rows) {
    if (!row.bucket || row.bucket === UNKNOWN_BUCKET) continue
    const key = normalizeLabel(row.bucket)
    groups.set(key, [...(groups.get(key) ?? []), row])
  }

  return [...groups.values()]
    .filter((group) => group.length > 1)
    .flatMap((group) => {
      const [canonical, ...variants] = [...group].sort((a, b) => b.leads - a.leads || a.bucket.localeCompare(b.bucket))
      return variants
        .filter((variant) => variant.leads > 0)
        .map((variant) => ({
          kind: `merge` as const,
          type,
          original: variant.bucket,
          alias: canonical.bucket,
          leads: variant.leads,
        }))
    })
}

export function buildCleanupSuggestions({
  sourceRows,
  mediumRows,
  sourceMediumRows,
  aliases,
  channelMappings,
}: CleanupInput): CleanupSuggestion[] {
  const suggestions: CleanupSuggestion[] = [
    ...findVariantMerges(sourceRows, `source`),
    ...findVariantMerges(mediumRows, `medium`),
  ]

  // Recorded even when the rename is later dropped as already-aliased: in that case the fix is in
  // flight rather than missing, so the pair should not be offered again as a mapping.
  const pairsFixedByRename = new Set<string>()

  for (const row of sourceRows) {
    if (row.leads <= 0 || !row.bucket || row.bucket === UNKNOWN_BUCKET) continue

    const canonical = canonicalSourceFor(row.bucket)
    if (!canonical || canonical === normalizeLabel(row.bucket)) continue

    const unknownPairs = sourceMediumRows.filter((pair) => {
      const [source, medium] = splitPair(pair.bucket)
      return (
        pair.leads > 0 &&
        !!medium &&
        medium !== UNKNOWN_BUCKET &&
        (pair.channel ?? UNKNOWN_BUCKET) === UNKNOWN_BUCKET &&
        normalizeLabel(source) === normalizeLabel(row.bucket)
      )
    })

    const resolved = unknownPairs.flatMap((pair) => {
      const [, medium] = splitPair(pair.bucket)
      const channel = lookupChannel(canonical, medium, channelMappings)
      return channel ? [{ bucket: pair.bucket, channel }] : []
    })

    // A spelling that stands alone earns a rename only when it buys a channel. Otherwise this is
    // an opinion about how someone should label their own traffic.
    const canonicalInUse = sourceRows.some((other) => normalizeLabel(other.bucket) === canonical)
    if (!canonicalInUse && resolved.length === 0) continue

    for (const pair of resolved) pairsFixedByRename.add(pair.bucket)

    const channels = new Set(resolved.map((pair) => pair.channel))
    suggestions.push({
      kind: `rename`,
      type: `source`,
      original: row.bucket,
      alias: canonical,
      leads: row.leads,
      ...(channels.size === 1 ? { resultingChannel: [...channels][0] } : {}),
    })
  }

  for (const pair of sourceMediumRows) {
    const [source, medium] = splitPair(pair.bucket)
    if (pair.leads <= 0 || pair.channel !== UNKNOWN_BUCKET) continue
    if (!source || source === UNKNOWN_BUCKET || !medium || medium === UNKNOWN_BUCKET) continue
    if (pairsFixedByRename.has(pair.bucket)) continue
    // The API's channel is only as fresh as the last reprocess, so a mapping saved a moment ago
    // still reads as Unknown here.
    if (lookupChannel(source, medium, channelMappings)) continue

    suggestions.push({ kind: `map`, source, medium, leads: pair.leads })
  }

  const aliased = new Set(
    aliases.filter((alias) => !!alias.original).map((alias) => `${alias.type}:${normalizeLabel(alias.original!)}`),
  )

  return suggestions
    .filter(
      (suggestion) =>
        suggestion.kind === `map` || !aliased.has(`${suggestion.type}:${normalizeLabel(suggestion.original)}`),
    )
    .sort((a, b) => b.leads - a.leads || a.kind.localeCompare(b.kind))
}
